import React from 'react'

import { BellLineIcon, DragHandleHorizontalIcon } from '@inno-ui/icons'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  APP_LIBRARY_TOUR_ID,
  CLUSTERIX_LOGO_TOUR_ID,
  NOTIFICATION_CENTER_TOUR_ID
} from 'shared/hooks/useOnboarding'
import { useLaunchpadStore } from 'shared/stores/launchpadStore'
import { useNotificationStore } from 'shared/stores/notificationStore'
import {
  selectNotificationBellUnreadCount,
  useUnreadCountStore
} from 'shared/stores/unreadCountStore'

import { UserProfile } from '../../UserProfile'
import { ActionButton } from '../components/ActionButton'
import { ClusterixLogo } from '../components/ClusterixLogo'
import styles from './TopDockMobile.module.scss'

const MotionLink = motion.create(Link)

interface TopDockMobileProps {
  handleLogoClick?: () => void
}

const TopDockMobile: React.FC<TopDockMobileProps> = ({ handleLogoClick }) => {
  const { t } = useTranslation('translation')

  const { notificationPaneState, toggleNotificationPane } =
    useNotificationStore()

  const isLaunchpadOpen = useLaunchpadStore(state => state.isOpen)
  const setIsLaunchpadOpen = useLaunchpadStore(
    state => state.setIsLaunchpadOpen
  )
  const notificationsUnreadCount = useUnreadCountStore(
    selectNotificationBellUnreadCount
  )

  const ACTION_BUTTONS = [
    {
      Icon: DragHandleHorizontalIcon,
      onClick: () => setIsLaunchpadOpen(prev => !prev),
      tourId: APP_LIBRARY_TOUR_ID,
      isActive: isLaunchpadOpen,
      ariaLabel: t('navbar.open_launchpad')
    },
    {
      Icon: BellLineIcon,
      onClick: toggleNotificationPane,
      tourId: NOTIFICATION_CENTER_TOUR_ID,
      ariaLabel: t('navbar.notifications'),
      isActive: notificationPaneState === 'opened',
      unreadCount: notificationsUnreadCount
    }
  ]

  return (
    <motion.header
      className={styles.TopDockMobile}
      initial={{
        y: -50,
        opacity: 0
      }}
      animate={{
        y: 0,
        opacity: 1
      }}
      exit={{
        y: -100,
        opacity: 0
      }}
    >
      <div className={styles.leftSection}>
        {ACTION_BUTTONS.map(
          (
            { Icon, onClick, ariaLabel, isActive, tourId, unreadCount },
            index
          ) => (
            <ActionButton
              key={`action-button-${index}`}
              index={index}
              Icon={Icon}
              onClick={onClick}
              ariaLabel={ariaLabel}
              isActive={isActive}
              tourId={tourId}
              unreadCount={unreadCount}
            />
          )
        )}
      </div>
      <MotionLink
        data-tour={CLUSTERIX_LOGO_TOUR_ID}
        to='/'
        onClick={handleLogoClick}
        className={styles.logo}
        aria-label={t('navbar.go_to_home')}
        whileHover={{
          scale: 1.05
        }}
      >
        <motion.span className={styles.logoIcon}>
          <ClusterixLogo />
        </motion.span>
      </MotionLink>

      <div className={styles.rightSection}>
        <UserProfile />
      </div>
    </motion.header>
  )
}

export { TopDockMobile }
